import {
    AppState,
    AuthenticationState,
    RenderTargetContext,
    RequiredAccess,
} from '@news-mono/web-common'
import React, { useContext, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { THEWEST_ALL } from './common'

interface EntitledToViewProps {
    requiredAccess: RequiredAccess
    /** slug to check against mmo referrer slug to allow access */
    articleSlug?: string
    entitled: () => React.ReactElement<any> | null
    notEntitled?: (
        loginStatusChecked: boolean,
    ) => React.ReactElement<any> | null
    onIsEntitledChange?: (event: {
        entitled: boolean
        loggedInStatusChecked: boolean
    }) => void
}

function entitledToViewPublication(
    requiredAccess: RequiredAccess,
    authentication: AuthenticationState,
    slug?: string,
) {
    /**
     * The EntitledToView component hides an article behind a breach screen if the user
     * isn't subscribed to see it. But we also have a registration wall for anonymous
     * users. The registration wall shows the first 3 paragraphs of an article so it
     * isn't handled in the EntitledToView code here but rather in ArticleBlockContent
     */
    if (requiredAccess.level !== 'subscriber') {
        return true
    }

    if (authentication.entitlements.indexOf(THEWEST_ALL) >= 0) {
        return true
    }

    if (typeof slug !== 'undefined' && authentication.bypassSlug === slug) {
        return true
    }

    // for topic paywall, we could pass in a list of entitlements and the primary (or secondary) topics.
    // then could look for an entitlement to 'thewest:all' or 'thewest:<topic>', or some other mapping
    return false
}

export const EntitledToView: React.FC<EntitledToViewProps> = ({
    requiredAccess,
    entitled,
    notEntitled,
    onIsEntitledChange,
    articleSlug,
}) => {
    const renderTarget = useContext(RenderTargetContext)
    const authentication = useSelector<AppState, AuthenticationState>(
        ({ authentication }) => authentication,
    )
    const isEntitled =
        entitledToViewPublication(
            requiredAccess,
            authentication,
            articleSlug,
        ) || renderTarget.renderTarget === 'preview'

    useEffect(() => {
        if (onIsEntitledChange) {
            onIsEntitledChange({
                entitled: isEntitled,
                loggedInStatusChecked: authentication.hasAttemptedValidation,
            })
        }
        // Ignoring eslint-disable-next-line react-hooks/exhaustive-deps for this effect
        // because it will continue to trigger. We can come back and look at useCallback and
        // resolving the issue. Any news deps should still be added
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEntitled, authentication.hasAttemptedValidation])

    return isEntitled
        ? entitled()
        : notEntitled
        ? notEntitled(authentication.hasAttemptedValidation)
        : null
}
